export const cameraMessages = {
  insecure:
    'Die Kamera kann über diese Verbindung nicht gestartet werden. Öffne die Lager-App über ihre sichere HTTPS-Adresse und erlaube anschließend den Kamerazugriff.',
  unsupported:
    'Dieser Browser unterstützt den Kamerazugriff hier nicht. Öffne die Lager-App in Safari oder einem aktuellen Browser über HTTPS.',
  permissionDenied:
    'Der Kamerazugriff wurde verweigert. Erlaube den Kamerazugriff in Safari beziehungsweise in den Website-Berechtigungen und versuche es erneut.',
  notFound:
    'Es wurde keine Kamera gefunden. Prüfe, ob am Gerät eine Kamera verfügbar ist.',
  inUse:
    'Die Kamera kann nicht gestartet werden. Sie wird möglicherweise bereits von einer anderen App verwendet.',
  constraint:
    'Die gewünschte Kameraeinstellung ist auf diesem Gerät nicht verfügbar.',
  security:
    'Der Browser blockiert den Kamerazugriff für diese Seite. Öffne die Lager-App über HTTPS und prüfe die Website-Berechtigungen.',
  aborted:
    'Der Kamerastart wurde abgebrochen. Schließe andere Kamera-Apps und versuche es erneut.',
  general:
    'Die Kamera konnte nicht gestartet werden. Prüfe die Verbindung, die Browser-Berechtigungen und die Kamera des Geräts.',
};

export const scannerStatuses = {
  idle: 'idle',
  starting: 'starting',
  ready: 'ready',
  error: 'error',
  stopped: 'stopped',
};

export function isLocalhost(hostname = window.location.hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

export function isCameraSecureContext(win = window, loc = window.location) {
  if (win?.isSecureContext === true) return true;
  if (loc?.protocol === 'https:') return true;
  return isLocalhost(loc?.hostname);
}

export function getCameraSupport(win = window, nav = navigator) {
  const secure = isCameraSecureContext(win, win?.location || window.location);

  if (!secure) {
    return { ok: false, reason: 'insecure', message: cameraMessages.insecure };
  }

  if (!nav?.mediaDevices || typeof nav.mediaDevices.getUserMedia !== 'function') {
    return {
      ok: false,
      reason: secure ? 'unsupported' : 'insecure',
      message: secure ? cameraMessages.unsupported : cameraMessages.insecure,
    };
  }

  return { ok: true };
}

export async function getCameraPermissionState(nav = navigator) {
  if (!nav?.permissions?.query) return 'unknown';

  try {
    const permission = await nav.permissions.query({ name: 'camera' });
    return permission?.state || 'unknown';
  } catch (error) {
    console.info('Kamera-Berechtigung konnte nicht vorab geprüft werden.', {
      name: error?.name,
    });
    return 'unknown';
  }
}

export function mapCameraError(error) {
  const name = error?.name;

  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return { reason: 'permissionDenied', message: cameraMessages.permissionDenied };
  }

  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return { reason: 'notFound', message: cameraMessages.notFound };
  }

  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return { reason: 'inUse', message: cameraMessages.inUse };
  }

  if (name === 'OverconstrainedError') {
    return { reason: 'constraint', message: cameraMessages.constraint };
  }

  if (name === 'SecurityError') {
    return { reason: 'security', message: cameraMessages.security };
  }

  if (name === 'AbortError') {
    return { reason: 'aborted', message: cameraMessages.aborted };
  }

  return { reason: 'general', message: cameraMessages.general };
}

export function stopCameraStream(video, codeReader) {
  try {
    codeReader?.reset?.();
  } catch (error) {
    console.info('Scanner konnte nicht vollständig zurückgesetzt werden.', {
      name: error?.name,
    });
  }

  const stream = video?.srcObject;
  if (stream?.getTracks) {
    stream.getTracks().forEach((track) => track.stop());
  }

  if (video) {
    try {
      video.pause?.();
    } catch (error) {
      console.info('Video-Pause wurde vom Browser nicht unterstützt.', { name: error?.name });
    }

    try {
      video.srcObject = null;
    } catch (error) {
      console.info('Videoquelle konnte nicht direkt gelöscht werden.', { name: error?.name });
    }

    video.removeAttribute?.('src');

    try {
      video.load?.();
    } catch (error) {
      console.info('Video-Reset wurde vom Browser nicht unterstützt.', { name: error?.name });
    }
  }
}

function createScannerOverlay({ title, secure }) {
  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', title);
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:999;background:#020617;display:flex;align-items:center;justify-content:center;';

  const video = document.createElement('video');
  video.playsInline = true;
  video.autoplay = true;
  video.muted = true;
  video.style.cssText = 'width:100%;height:100%;object-fit:cover;';

  const panel = document.createElement('div');
  panel.style.cssText =
    'position:fixed;left:16px;right:16px;bottom:18px;z-index:1000;background:rgba(255,255,255,0.96);border-radius:14px;padding:14px;box-shadow:0 8px 30px rgba(0,0,0,0.25);font-family:Arial,sans-serif;';

  const status = document.createElement('div');
  status.textContent = 'Kamera wird gestartet...';
  status.style.cssText = 'font-size:14px;color:#334155;margin-bottom:10px;line-height:1.4;';

  const hint = document.createElement('div');
  hint.textContent = secure
    ? 'Erlaube bei Nachfrage den Kamerazugriff in Safari.'
    : 'Öffne die Seite über HTTPS und erlaube anschließend den Kamerazugriff in Safari.';
  hint.style.cssText = 'font-size:12px;color:#64748b;margin-bottom:12px;line-height:1.4;';

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:10px;';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = 'Schließen';
  closeButton.style.cssText =
    'flex:1;padding:12px;background:#f1f5f9;border:none;border-radius:10px;font-weight:700;cursor:pointer;';

  const retryButton = document.createElement('button');
  retryButton.type = 'button';
  retryButton.textContent = 'Erneut versuchen';
  retryButton.style.cssText =
    'display:none;flex:1;padding:12px;background:#f59e0b;color:white;border:none;border-radius:10px;font-weight:700;cursor:pointer;';

  actions.append(closeButton, retryButton);
  panel.append(status, hint, actions);
  overlay.append(video, panel);
  document.body.appendChild(overlay);

  return { overlay, video, status, hint, closeButton, retryButton };
}

function setOverlayStarting(ui, secure, onStateChange) {
  ui.status.textContent = 'Kamera wird gestartet...';
  ui.status.style.display = 'block';
  ui.hint.textContent = secure
    ? 'Erlaube bei Nachfrage den Kamerazugriff in Safari.'
    : 'Öffne die Seite über HTTPS und erlaube anschließend den Kamerazugriff in Safari.';
  ui.hint.style.display = 'block';
  ui.retryButton.style.display = 'none';
  onStateChange?.({ status: scannerStatuses.starting, message: 'Kamera wird gestartet...' });
}

function setOverlayReady(ui, onStateChange) {
  ui.status.textContent = '';
  ui.status.style.display = 'none';
  ui.hint.textContent = '';
  ui.hint.style.display = 'none';
  ui.retryButton.style.display = 'none';
  onStateChange?.({ status: scannerStatuses.ready, message: '' });
}

export function getScannerPageHint(scanner) {
  if (scanner?.status !== scannerStatuses.error) return '';
  if (scanner.reason !== 'insecure') return '';
  return 'iPhone-Hinweis: Öffne die Seite über HTTPS. Erlaube anschließend den Kamerazugriff in Safari.';
}

export function isScannerBusy(scanner) {
  return [scannerStatuses.starting, scannerStatuses.ready, scannerStatuses.error].includes(scanner?.status);
}

export async function startBarcodeScanner({ title, onResult, onClose, onError, onStateChange, createCodeReader }) {
  const support = getCameraSupport();
  if (!support.ok) {
    onError?.({ ...support, overlay: false });
    return null;
  }

  const permissionState = await getCameraPermissionState();
  if (permissionState === 'denied') {
    const denied = { reason: 'permissionDenied', message: cameraMessages.permissionDenied };
    onError?.({ ...denied, overlay: false });
    return null;
  }

  let codeReader;
  if (createCodeReader) {
    codeReader = createCodeReader();
  } else {
    const { BrowserMultiFormatReader } = await import('@zxing/library');
    codeReader = new BrowserMultiFormatReader();
  }
  const secure = isCameraSecureContext();
  const ui = createScannerOverlay({ title, secure });
  let stopped = false;
  let ready = false;

  const markReady = () => {
    if (stopped || ready) return;
    ready = true;
    setOverlayReady(ui, onStateChange);
  };

  ['loadedmetadata', 'canplay', 'playing'].forEach((eventName) => {
    ui.video.addEventListener(eventName, markReady, { once: true });
  });

  const stop = () => {
    if (stopped) return;
    stopped = true;
    stopCameraStream(ui.video, codeReader);
    ui.overlay.remove();
    onStateChange?.({ status: scannerStatuses.stopped, message: '' });
    onClose?.();
  };

  const showError = (mapped) => {
    ui.status.textContent = mapped.message;
    ui.status.style.display = 'block';
    ui.hint.textContent = mapped.reason === 'insecure'
      ? 'Öffne die Seite über HTTPS und erlaube anschließend den Kamerazugriff in Safari.'
      : '';
    ui.hint.style.display = mapped.reason === 'insecure' ? 'block' : 'none';
    ui.retryButton.style.display = 'block';
    onStateChange?.({ status: scannerStatuses.error, message: mapped.message, reason: mapped.reason });
    onError?.({ ...mapped, overlay: true });
  };

  ui.closeButton.onclick = stop;
  ui.retryButton.onclick = () => {
    ready = false;
    setOverlayStarting(ui, secure, onStateChange);
    runDecode();
  };

  const handleResult = async (result) => {
    if (!result || stopped) return;
    const text = result.getText();
    stop();
    await onResult?.(text);
  };

  const runDecode = async () => {
    if (stopped) return;
    setOverlayStarting(ui, secure, onStateChange);
    stopCameraStream(ui.video, codeReader);

    try {
      await codeReader.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } }, audio: false },
        ui.video,
        handleResult
      );
    } catch (error) {
      if (stopped) return;
      if (error?.name !== 'OverconstrainedError') {
        console.info('Kamerastart fehlgeschlagen.', { name: error?.name });
        showError(mapCameraError(error));
        return;
      }

      try {
        await codeReader.decodeFromConstraints({ video: true, audio: false }, ui.video, handleResult);
      } catch (fallbackError) {
        if (stopped) return;
        console.info('Kamera-Fallback fehlgeschlagen.', { name: fallbackError?.name });
        showError(mapCameraError(fallbackError));
      }
    }
  };

  runDecode();
  return { stop };
}
